import { Box, Button, Card, Heading, HStack, Separator, Tabs, Text, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import {
  BarChart3,
  Bot,
  Cloud,
  Database,
  ExternalLink,
  Layers,
  MapPin,
  Network,
  Radio,
  Users,
} from 'lucide-react'
import PlatformArchitecture from '@/components/architecture/PlatformArchitecture'
import PasswordReveal from '@/components/PasswordReveal'
import { PageHeader } from '@/components/shell'

export const Route = createFileRoute('/_app/demoinfo')({
  component: DemoInfoPage,
})

interface InfoSectionProps {
  title: string
  children: React.ReactNode
}

// Every section on the page renders through this, so the card treatment here is what
// lifts all nine tabs rather than restyling each one.
function InfoSection({ title, children }: InfoSectionProps) {
  return (
    <Box
      w="100%"
      p={5}
      borderWidth="1px"
      borderRadius="lg"
      bg="bg.subtle"
      borderLeftWidth="3px"
      borderLeftColor="brand.solid"
    >
      <VStack align="start" gap={2} w="100%">
        <Text fontWeight="bold" fontSize="md" letterSpacing="tight">
          {title}
        </Text>
        {children}
      </VStack>
    </Box>
  )
}

interface CredentialItemProps {
  label: string
  value: string
}

function CredentialItem({ label, value }: CredentialItemProps) {
  return (
    <HStack gap={2} align="baseline" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="medium" color="content.secondary" minW="90px">
        {label}
      </Text>
      {/* Monospace: these are values people copy, and endpoints and passwords are
          where a transcription error costs the most. */}
      <Text fontSize="sm" fontFamily="mono">
        {value}
      </Text>
    </HStack>
  )
}

function DemoInfoPage() {
  const tabs = [
    { id: 'pulse-edge', label: 'Smart Manufacturing', icon: MapPin },
    { id: 'shared', label: 'Shared Broker', icon: Network },
    { id: 'cloud', label: 'Platform', icon: Cloud },
    { id: 'companion', label: 'ESE Companion', icon: Users },
    { id: 'pgadmin', label: 'PG Admin', icon: Database },
    { id: 'grafana', label: 'Grafana', icon: BarChart3 },
    { id: 'dhrm', label: 'DataHub Registry', icon: Layers },
    { id: 'recplay', label: 'RecPlay', icon: Radio },
    { id: 'llm', label: 'Local LLM', icon: Bot },
  ]

  // These `name` values build the hostnames below, so they must match the location names
  // in the Ansible scenario exactly - "sanfrancisco" has no hyphen, and an earlier
  // "san-francisco" here produced a link that did not resolve. Nuremberg and Dallas were
  // retired 2026-08-21 and their Edge deployments no longer exist.
  const dedicatedEdgeLocations = [
    { name: 'sanfrancisco', displayName: 'San Francisco' },
    { name: 'tokyo', displayName: 'Tokyo' },
    { name: 'lisbon', displayName: 'Lisbon (Packaging)' },
  ]

  const sharedEdgeLocations = [
    { name: 'aberdeen', displayName: 'Aberdeen' },
    { name: 'houston', displayName: 'Houston' },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <Text
        fontSize="sm"
        fontWeight="bold"
        color="brand.solid"
        textTransform="uppercase"
        letterSpacing="wide"
        mb={1}
      >
        Tools
      </Text>
      <PageHeader
        title="Demo Platform Info"
        subtitle="Connection details and credentials for all demo services"
      />

      <VStack align="stretch" gap={10}>
        {/* Replaces a row of chips that repeated the tab labels immediately above the
            tab strip. What was missing from this page was how the pieces fit together. */}
        <Card.Root>
          <Card.Body p={6}>
            <VStack align="stretch" gap={4}>
              <VStack align="start" gap={1}>
                <Text fontWeight="bold" fontSize="lg">
                  Demo Architecture
                </Text>
                <Text fontSize="sm" color="content.secondary">
                  What produces the data, what carries it, where it lands and who reads it. Drawn
                  from the running deployment - where intent and reality differ, this shows reality.
                </Text>
              </VStack>
              <PlatformArchitecture />
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Info Tabs */}
        <Card.Root>
          <Card.Body p={6}>
            <Tabs.Root defaultValue="pulse-edge">
              <Tabs.List mb={6}>
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <Tabs.Trigger key={tab.id} value={tab.id}>
                      <HStack gap={2}>
                        <Icon size={16} />
                        <Text>{tab.label}</Text>
                      </HStack>
                    </Tabs.Trigger>
                  )
                })}
              </Tabs.List>

              {/* Pulse Edge Tab */}
              <Tabs.Content value="pulse-edge" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    Find below the information to access the various resources for the Pulse Smart
                    Manufacturing demo. Each Edge location runs{' '}
                    <a
                      href="https://www.hivemq.com/products/hivemq-edge/"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: 'var(--chakra-colors-brand-solid)',
                        textDecoration: 'underline',
                      }}
                    >
                      HiveMQ Edge
                    </a>{' '}
                    with protocol adapters and bridges to the central broker. San Francisco and
                    Tokyo use Modbus; Lisbon reads OPC UA from the RecPlay emulations.
                  </Text>

                  <Separator />

                  <InfoSection title="Central Broker (HQ)">
                    <Heading size="sm" mt={2}>
                      Control Center
                    </Heading>
                    <a
                      href="https://cc-central-001.demo.hivemq.io/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--chakra-colors-brand-solid)' }}
                    >
                      https://cc-central-001.demo.hivemq.io/
                    </a>
                    <CredentialItem label="Username (Viewer)" value="dashboardviewer" />
                    <PasswordReveal password="PnNp8DDz69@a" />
                    <CredentialItem label="Username (Admin)" value="admincc" />
                    <PasswordReveal password="PnNp8DDz69@a" />
                    <Separator />
                    <Heading size="sm" mt={2}>
                      MQTT
                    </Heading>
                    <CredentialItem label="Endpoint" value="central-001.demo.hivemq.io" />
                    <CredentialItem label="Port" value="8883" />
                    <CredentialItem label="Username" value="unsreader" />
                    <PasswordReveal password="unsreader" />
                    <Separator />
                    <Heading size="sm" mt={2}>
                      WebSocket
                    </Heading>
                    <CredentialItem label="Endpoint" value="ws-central-001.demo.hivemq.io" />
                    <CredentialItem label="Port" value="443" />
                    <CredentialItem label="Path" value="/mqtt" />
                    <CredentialItem label="Username" value="unsreader" />
                    <PasswordReveal password="unsreader" />
                    <Text fontWeight="medium" mt={2}>
                      Available UNS:
                    </Text>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• Smart Manufacturing: ecorp/#</Text>
                      <Text fontSize="sm">• Lisbon packaging: BBCorp/Lisbon/Packaging/#</Text>
                      <Text fontSize="sm">• SparkplugB: spBv1.0/#</Text>
                      <Text fontSize="sm">• After SPB DataHub activation: MunichPlant/#</Text>
                    </VStack>
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      The industry verticals — ConnCorp/#, WindCorp/#, OGCorp/#, HPSCorp/#,
                      HTCCorp/# and Biomed/# — are still published here by the in-cluster
                      simulators, but they are migrating to the shared broker. See the Shared Broker
                      tab for the target state.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Edge deployments">
                    <Heading size="sm" mt={2}>
                      Control Panel Credentials (All Locations)
                    </Heading>
                    <CredentialItem label="Username" value="adminedge" />
                    <PasswordReveal password="SEZkN!ZHB6" />

                    <Heading size="sm" mt={2}>
                      MQTT Credentials (All Locations)
                    </Heading>
                    <CredentialItem label="Username" value="adminedge" />
                    <PasswordReveal password="SEZkN!ZHB6" />
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      All locations share these credentials, Lisbon included. Verified against the
                      running deployments.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Lisbon Packaging Line">
                    <Text fontSize="sm" color="content.secondary">
                      Two packaging lines of four machines (fill-01, cap-01, label-01, pack-01),
                      publishing 92 tags per line. HiveMQ Edge reads them over OPC UA from the
                      RecPlay emulations and publishes straight into the UNS tree.
                    </Text>
                    <Heading size="sm" mt={2}>
                      Topics
                    </Heading>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">
                        •
                        BBCorp/Lisbon/Packaging/&#123;line&#125;/&#123;machine&#125;/&#123;Tag&#125;
                      </Text>
                      <Text fontSize="sm">• Lines: line-a, line-b (184 tags total)</Text>
                    </VStack>
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      Counters and accumulators (ProcessedCount, RejectCount, EnergyKwh, every
                      AccTime*) are not trustworthy: the emulations drive them with sequence
                      generators, so they all climb in lockstep and a computed reject rate comes out
                      at 100%. The rate and level tags are sound. The Grafana panels affected are
                      marked.
                    </Text>
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      <Text as="span" fontWeight="medium">
                        kpi
                      </Text>{' '}
                      and{' '}
                      <Text as="span" fontWeight="medium">
                        agent
                      </Text>{' '}
                      are reserved in the machine position for computed outputs - do not publish
                      machine tags there.
                    </Text>
                    <Heading size="sm" mt={2}>
                      OPC UA source
                    </Heading>
                    <CredentialItem label="Endpoint" value="opc.tcp://opcua.demo.hivemq.io:4840" />
                    <CredentialItem label="Line A" value="Lisbon_Packaging_A (ns=11)" />
                    <CredentialItem label="Line B" value="Lisbon_Packaging_B (ns=12)" />
                    <Heading size="sm" mt={2}>
                      Dashboards &amp; storage
                    </Heading>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">
                        • Grafana: Packaging Line Performance, Packaging Quality
                      </Text>
                      <Text fontSize="sm">
                        • TimescaleDB (in-cluster only):
                        timescaledb-packaging.packaging.svc.cluster.local:5432
                      </Text>
                    </VStack>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Edge Locations">
                    <Text fontSize="sm" color="content.secondary" mb={3}>
                      These locations are dedicated and should not be modified without coordination.
                    </Text>
                    <Box w="100%" overflowX="auto">
                      <VStack align="start" gap={4} w="100%">
                        {dedicatedEdgeLocations.map((location) => (
                          <Box key={location.name} w="100%" p={4} bg="bg.subtle" borderRadius="md">
                            <VStack align="start" gap={2}>
                              <HStack gap={2}>
                                <MapPin size={16} color="var(--chakra-colors-brand-solid)" />
                                <Text fontWeight="bold">{location.displayName}</Text>
                              </HStack>
                              <HStack gap={4} flexWrap="wrap">
                                <VStack align="start" gap={1}>
                                  <Text fontSize="xs" color="content.secondary">
                                    Control Center
                                  </Text>
                                  <a
                                    href={`https://${location.name}-cc-edge.demo.hivemq.io`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: 'var(--chakra-colors-brand-solid)',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {location.name}-cc-edge.demo.hivemq.io
                                  </a>
                                </VStack>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="xs" color="content.secondary">
                                    MQTT (WebSocket Secure)
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Endpoint:
                                    </Text>{' '}
                                    {location.name}-ws-edge.demo.hivemq.io
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Port:
                                    </Text>{' '}
                                    443
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Path:
                                    </Text>{' '}
                                    /mqtt
                                  </Text>
                                </VStack>
                              </HStack>
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* Shared Broker Tab */}
              <Tabs.Content value="shared" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    The shared HiveMQ Enterprise broker (shared-001) carries the industry verticals.
                    The central broker is reserved for the Smart Manufacturing demo, so anything
                    else belongs here. Two HiveMQ Edge sites bridge field telemetry in (Aberdeen for
                    offshore wind, BESS and hydro; Houston for solar, oil &amp; gas), and it is
                    persisted into TimescaleDB for the Grafana dashboards.
                  </Text>

                  <Separator />

                  <InfoSection title="Broker">
                    <Heading size="sm" mt={2}>
                      Control Center
                    </Heading>
                    <a
                      href="https://cc-shared-001.demo.hivemq.io/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--chakra-colors-brand-solid)' }}
                    >
                      https://cc-shared-001.demo.hivemq.io/
                    </a>
                    <CredentialItem label="Username" value="admin" />
                    <PasswordReveal password="hivemq" />
                    <Separator />
                    <Heading size="sm" mt={2}>
                      MQTT
                    </Heading>
                    <CredentialItem label="Endpoint" value="shared-001.demo.hivemq.io" />
                    <CredentialItem label="Port" value="8883" />
                    <CredentialItem label="WebSocket" value="ws-shared-001.demo.hivemq.io:443" />
                    <CredentialItem label="Username" value="admin" />
                    <PasswordReveal password="password" />
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      The MQTT credential is not the Control Center one. Verified against the
                      running broker: the Control Center password is rejected on 8883 as “not
                      authorised”.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="UNS roots">
                    <Text fontSize="sm" color="content.secondary">
                      Live today, bridged from the two Edge sites:
                    </Text>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• Energy &amp; Oil &amp; Gas: ecorp/#</Text>
                    </VStack>
                    <Text fontSize="sm" color="content.secondary" mt={3}>
                      Migrated from the in-cluster simulators into RecPlay and pointed at this
                      broker. Configured but not yet publishing — the in-cluster simulators are
                      still the live source on the central broker, so expect these trees to be empty
                      here until the cutover:
                    </Text>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• Pharma: Biomed/# (2601 topics)</Text>
                      <Text fontSize="sm">• Connected Assets: HTCCorp/# (300)</Text>
                      <Text fontSize="sm">• Logistics: HPSCorp/# (234)</Text>
                      <Text fontSize="sm">• Connected Cars: ConnCorp/# (204)</Text>
                      <Text fontSize="sm">• Wind Energy: WindCorp/# (147)</Text>
                      <Text fontSize="sm">• Oil &amp; Gas: OGCorp/# (42)</Text>
                    </VStack>
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      The *-to-kafka mappings for these roots are still bound to the central broker,
                      so they follow the source rather than this broker.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Edge deployments">
                    <Heading size="sm" mt={2}>
                      Control Panel Credentials (All Locations)
                    </Heading>
                    <CredentialItem label="Username" value="adminedge" />
                    <PasswordReveal password="SEZkN!ZHB6" />

                    <Heading size="sm" mt={2}>
                      MQTT Credentials (All Locations)
                    </Heading>
                    <CredentialItem label="Username" value="adminedge" />
                    <PasswordReveal password="SEZkN!ZHB6" />
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Edge Locations">
                    <Text fontSize="sm" color="content.secondary" mb={3}>
                      Two Pulse Edge sites bridge field telemetry into the shared broker.
                    </Text>
                    <Box w="100%" overflowX="auto">
                      <VStack align="start" gap={4} w="100%">
                        {sharedEdgeLocations.map((location) => (
                          <Box key={location.name} w="100%" p={4} bg="bg.subtle" borderRadius="md">
                            <VStack align="start" gap={2}>
                              <HStack gap={2}>
                                <MapPin size={16} color="var(--chakra-colors-brand-solid)" />
                                <Text fontWeight="bold">{location.displayName}</Text>
                              </HStack>
                              <HStack gap={4} flexWrap="wrap">
                                <VStack align="start" gap={1}>
                                  <Text fontSize="xs" color="content.secondary">
                                    Control Center
                                  </Text>
                                  <a
                                    href={`https://${location.name}-cc-edge.demo.hivemq.io`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: 'var(--chakra-colors-brand-solid)',
                                      fontSize: '14px',
                                    }}
                                  >
                                    {location.name}-cc-edge.demo.hivemq.io
                                  </a>
                                </VStack>
                                <VStack align="start" gap={1}>
                                  <Text fontSize="xs" color="content.secondary">
                                    MQTT (WebSocket Secure)
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Endpoint:
                                    </Text>{' '}
                                    {location.name}-ws-edge.demo.hivemq.io
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Port:
                                    </Text>{' '}
                                    443
                                  </Text>
                                  <Text fontSize="sm">
                                    <Text as="span" fontWeight="medium">
                                      Path:
                                    </Text>{' '}
                                    /mqtt
                                  </Text>
                                </VStack>
                              </HStack>
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              <Tabs.Content value="cloud" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    The HiveMQ Platform console for the permanent demo. Pulse and the managed
                    deployments are reached through the same account.
                  </Text>

                  <Separator />

                  <InfoSection title="Console">
                    <a
                      href="https://app.hivemq.com/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--chakra-colors-brand-solid)' }}
                    >
                      https://app.hivemq.com/
                    </a>
                    <CredentialItem label="Username" value="permanent-demo@hivemq.com" />
                    <PasswordReveal password="sCTamEdfYnoAt1bNRPFa" />
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* ESE Companion Tab */}
              <Tabs.Content value="companion" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    ESE Companion is a standalone web application for managing{' '}
                    <a
                      href="https://www.hivemq.com/extension/enterprise-security-extension/"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: 'var(--chakra-colors-brand-solid)',
                        textDecoration: 'underline',
                      }}
                    >
                      HiveMQ Enterprise Security Extension
                    </a>{' '}
                    users, roles, and permissions. It provides a user-friendly interface to
                    configure authentication and authorization policies for the HiveMQ broker.
                  </Text>

                  <Separator />

                  <InfoSection title="Web Interface">
                    <Text fontSize="sm" color="content.secondary" mb={3}>
                      Access the ESE Companion dashboard to manage security configurations.
                    </Text>
                    <a
                      href="https://companion.demo.hivemq.io"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button
                        bg="brand.solid"
                        color="gray.800"
                        _hover={{ bg: 'brand.fg' }}
                        size="sm"
                      >
                        <HStack gap={2}>
                          <Text>Open ESE Companion</Text>
                          <ExternalLink size={14} />
                        </HStack>
                      </Button>
                    </a>
                    <VStack align="start" gap={1} mt={4}>
                      <CredentialItem label="Username" value="adminese" />
                      <PasswordReveal password="Ese@dmin" />
                    </VStack>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Features">
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• User management (create, edit, delete users)</Text>
                      <Text fontSize="sm">• Role-based access control configuration</Text>
                      <Text fontSize="sm">• Permission management for MQTT topics</Text>
                      <Text fontSize="sm">• Real-time sync with HiveMQ ESE configuration</Text>
                    </VStack>
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* PG Admin Tab */}
              <Tabs.Content value="pgadmin" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    Find below all the information related to PG Admin running on the permanent
                    demo.
                  </Text>

                  <Separator />

                  <InfoSection title="UI">
                    <a
                      href="https://pgadmin.demo.hivemq.io"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--chakra-colors-brand-solid)' }}
                    >
                      https://pgadmin.demo.hivemq.io
                    </a>
                    <CredentialItem label="Username" value="admin@hivemq.com" />
                    <PasswordReveal password="SuperAdminHiveMQ" />
                  </InfoSection>

                  <Separator />

                  <InfoSection title="PostgreSQL Managed Instance">
                    <PasswordReveal password="xuc8GQE6cbx*emp!rkx" />
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* Grafana Tab */}
              <Tabs.Content value="grafana" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    Find below all the information related to Grafana running on the permanent demo.
                  </Text>

                  <Separator />

                  <InfoSection title="UI">
                    <a
                      href="https://grafana.demo.hivemq.io"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--chakra-colors-brand-solid)' }}
                    >
                      https://grafana.demo.hivemq.io
                    </a>
                    <CredentialItem label="Username" value="admin" />
                    <PasswordReveal password="hivemq" />
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* DataHub Registry Manager Tab */}
              <Tabs.Content value="dhrm" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    Find below all the information related to the{' '}
                    <a
                      href="https://dhrm.demo.hivemq.io/"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: 'var(--chakra-colors-brand-solid)',
                        textDecoration: 'underline',
                      }}
                    >
                      DataHub Registry Manager
                    </a>{' '}
                    running on the permanent demo. DHRM provides a central registry for managing
                    DataHub schemas, transformations, and policies.
                  </Text>

                  <Separator />

                  <InfoSection title="Web Interface">
                    <a
                      href="https://dhrm.demo.hivemq.io/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button
                        bg="brand.solid"
                        color="gray.800"
                        _hover={{ bg: 'brand.fg' }}
                        size="sm"
                      >
                        <HStack gap={2}>
                          <Text>Open DataHub Registry</Text>
                          <ExternalLink size={14} />
                        </HStack>
                      </Button>
                    </a>
                    <VStack align="start" gap={1} mt={4}>
                      <CredentialItem label="Username" value="demo-stories@hivemq.com" />
                      <PasswordReveal password="D@t@HubR3p0" />
                    </VStack>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Features">
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• Schema Registry for DataHub transformations</Text>
                      <Text fontSize="sm">• Version control for policies and configurations</Text>
                      <Text fontSize="sm">• Module management and deployment</Text>
                      <Text fontSize="sm">• Activity logging and audit trail</Text>
                    </VStack>
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* RecPlay Tab */}
              <Tabs.Content value="recplay" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    RecPlay records and replays OT traffic and hosts the protocol emulations the
                    Edge instances read from. It is the OPC UA source behind the Lisbon packaging
                    line and the Aberdeen hydro tags.
                  </Text>

                  <Separator />

                  <InfoSection title="Web Interface">
                    <a
                      href="https://recplay.demo.hivemq.io"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button
                        bg="brand.solid"
                        color="gray.800"
                        _hover={{ bg: 'brand.fg' }}
                        size="sm"
                      >
                        <HStack gap={2}>
                          <Text>Open RecPlay</Text>
                          <ExternalLink size={14} />
                        </HStack>
                      </Button>
                    </a>
                    <Text fontSize="sm" color="content.secondary" mt={3}>
                      RecPlay is deployed from its own Helm chart, so its console credentials are
                      not held in this platform's configuration - ask the Solution Engineering team
                      rather than expecting them here.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Emulation Endpoints">
                    <CredentialItem label="OPC UA" value="opc.tcp://opcua.demo.hivemq.io:4840" />
                    <CredentialItem label="Modbus TCP" value="modbus.demo.hivemq.io:5020" />
                    <CredentialItem
                      label="Coordination broker"
                      value="mqtts://cb.demo.hivemq.io:8883"
                    />
                    <Text fontSize="sm" color="content.secondary" mt={2}>
                      Each emulation has its own{' '}
                      <Text as="span" fontWeight="medium">
                        emulation-N
                      </Text>{' '}
                      user, where N matches the OPC UA namespace index the client must use. Node ids
                      are UUIDs assigned at creation, not readable paths, and namespace indices can
                      shift if an emulation is recreated.
                    </Text>
                  </InfoSection>
                </VStack>
              </Tabs.Content>

              {/* Local LLM Tab */}
              <Tabs.Content value="llm" mb="10px">
                <VStack align="start" gap={6}>
                  <Text color="content.secondary">
                    A self-hosted, OpenAI-compatible inference endpoint running on the cluster's GPU
                    node, with a small chat console for testing it. Nothing leaves the platform,
                    which is what makes it usable for customer-data demos.
                  </Text>

                  <Separator />

                  <InfoSection title="Chat Console (Open WebUI)">
                    <a
                      href="https://llm.demo.hivemq.io"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button
                        bg="brand.solid"
                        color="gray.800"
                        _hover={{ bg: 'brand.fg' }}
                        size="sm"
                      >
                        <HStack gap={2}>
                          <Text>Open LLM Console</Text>
                          <ExternalLink size={14} />
                        </HStack>
                      </Button>
                    </a>
                    <VStack align="start" gap={1} mt={4}>
                      <CredentialItem label="Username" value="admin@hivemq.com" />
                      <PasswordReveal password="erNzjGg7AfFHH3P6xii4KXyl" />
                    </VStack>
                    <Text fontSize="sm" color="content.secondary" mt={3}>
                      Self-registration is disabled, so this is the only account. It lives in the
                      console's own database rather than in platform configuration - if it is ever
                      lost, the volume has to be wiped to create a new one.
                    </Text>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="OpenAI-Compatible API">
                    <Text fontSize="sm" color="content.secondary">
                      In-cluster only and deliberately not published - point workloads running on
                      the platform at it. No API key is required.
                    </Text>
                    <CredentialItem
                      label="Base URL"
                      value="http://vllm.demo-ai.svc.cluster.local:8000/v1"
                    />
                    <CredentialItem label="Model" value="qwen3-8b (Qwen/Qwen3-8B)" />
                    <CredentialItem label="Context" value="16384 tokens" />
                    <Text fontWeight="medium" mt={2}>
                      Supported routes:
                    </Text>
                    <VStack align="start" gap={1} pl={4}>
                      <Text fontSize="sm">• /v1/models</Text>
                      <Text fontSize="sm">• /v1/chat/completions</Text>
                      <Text fontSize="sm">• /v1/completions</Text>
                      <Text fontSize="sm">
                        • Guided decoding via response_format (enforces a JSON schema)
                      </Text>
                    </VStack>
                  </InfoSection>

                  <Separator />

                  <InfoSection title="Worth knowing">
                    <VStack align="start" gap={2}>
                      <Text fontSize="sm">
                        Qwen3 is a hybrid reasoning model and emits a thinking block by default,
                        which can consume the whole token budget before any answer appears. Pass{' '}
                        <Text as="span" fontWeight="medium">
                          enable_thinking: false
                        </Text>{' '}
                        in chat_template_kwargs for short, direct replies.
                      </Text>
                      <Text fontSize="sm">
                        There is no embeddings model here - the endpoint answers "The model does not
                        support Embeddings API" for embedding requests.
                      </Text>
                      <Text fontSize="sm">
                        The GPU node is the most expensive resource on the platform, so check with
                        the team before assuming it runs around the clock.
                      </Text>
                    </VStack>
                  </InfoSection>
                </VStack>
              </Tabs.Content>
            </Tabs.Root>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
